import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://cafe-app-u6w5.onrender.com/api';

// ── Base axios instance ───────────────────────────────────────────────────────
const api = axios.create({ baseURL: API_BASE, timeout: 15000 });

// Token resolution — admin > staff > none
// NOTE: cafe_super_token yahan intentionally NAHI hai — super token admin API mein
// reject hota hai (type mismatch → 401 loop), superApi apna token khud handle karta hai.
const getActiveToken = () =>
  localStorage.getItem('cafe_admin_token') ||
  localStorage.getItem('cafe_staff_token') ||
  null;

api.interceptors.request.use((config) => {
  const token = getActiveToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    if (status === 401) {
      localStorage.removeItem('cafe_admin_token');
      localStorage.removeItem('cafe_admin_user');
      localStorage.removeItem('cafe_staff_token');
      localStorage.removeItem('cafe_staff_user');
      const path = window.location.pathname;
      if (path.startsWith('/super-admin')) {
        // don't redirect, SuperAdminAuthContext handles this
      } else if (path.includes('/admin') || path.includes('/staff')) {
        // Extract slug from path like /cafe-uk/admin
        const slug = path.split('/')[1];
        window.location.href = slug ? `/${slug}/admin/login` : '/admin/login';
      }
    }
    if (status === 429) {
      toast.error('⏳ Too many requests. Please wait a moment.', { id: 'rate-limit', duration: 4000 });
    } else if (status === 500) {
      toast.error('🔧 Server error. Please try again shortly.', { id: 'server-error', duration: 4000 });
    } else if (!err.response) {
      toast.error('📡 No internet connection.', { id: 'network-error', duration: 5000 });
    }
    return Promise.reject(err);
  }
);

// ── Super Admin separate instance ─────────────────────────────────────────────
const superApi = axios.create({ baseURL: API_BASE, timeout: 15000 });
superApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('cafe_super_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
superApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('cafe_super_token');
      localStorage.removeItem('cafe_super_user');
      window.location.href = '/super-admin/login';
    }
    return Promise.reject(err);
  }
);

// ── Public restaurant APIs (scoped by slug) ────────────────────────────────────
export const createPublicApi = (slug) => {
  const base = `${API_BASE}/r/${slug}`;
  const pub = axios.create({ baseURL: base, timeout: 15000 });

  return {
    menu: {
      getAll:        (params) => pub.get('/menu', { params }),
      getCategories: ()       => pub.get('/menu/categories'),
      getOne:        (id)     => pub.get(`/menu/${id}`),
    },
    orders: {
      create:  (data) => pub.post('/orders', data),
      track:   (data) => pub.post('/orders/track', data),
      history: (data) => pub.post('/orders/history', data),
    },
    reservations: {
      create: (data) => pub.post('/reservations', data),
    },
    gallery: {
      getAll: (params) => pub.get('/gallery', { params }),
    },
    settings: {
      get: () => pub.get('/settings'),
    },
    reviews: {
      getAll: (menuItemId) => pub.get(`/reviews/${menuItemId}`),
      create: (menuItemId, data) => pub.post(`/reviews/${menuItemId}`, data),
    },
    coupons: {
      validate: (data) => pub.post('/coupons/validate', data),
    },
    contact: {
      send: (data) => pub.post('/contact', data),
    },
  };
};

// ── Admin APIs (JWT carries restaurant_id) ─────────────────────────────────────
export const adminApi = {
  login:        (data) => api.post('/admin/login', data),
  register:     (data) => api.post('/admin/register', data),
  getMe:        ()     => api.get('/admin/me'),
  getDashboard: ()     => api.get('/admin/dashboard'),
  getAdmins:    ()     => api.get('/admin/list'),
  deleteAdmin:         (id)       => api.delete(`/admin/${id}`),
  changeStaffPassword: (id, data) => api.put(`/admin/${id}/password`, data),
  // Tables
  getTables:    ()     => api.get('/admin/tables'),
  addTable:     (data) => api.post('/admin/tables', data),
  deleteTable:  (id)   => api.delete(`/admin/tables/${id}`),
};

export const menuApi = {
  getAll:         (params)       => api.get('/admin/menu', { params }),
  getCategories:  ()             => api.get('/admin/menu/categories'),
  getOne:         (id)           => api.get(`/admin/menu/${id}`),
  create:         (formData)     => api.post('/admin/menu', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:         (id, formData) => api.put(`/admin/menu/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:         (id)           => api.delete(`/admin/menu/${id}`),
  createCategory: (data)         => api.post('/admin/menu/categories/new', data),
  deleteCategory: (id)           => api.delete(`/admin/menu/categories/${id}`),
};

export const orderApi = {
  getAll:       (params)       => api.get('/admin/orders', { params }),
  getOne:       (id)           => api.get(`/admin/orders/${id}`),
  updateStatus: (id, status)   => api.put(`/admin/orders/${id}`, { status }),
  delete:       (id)           => api.delete(`/admin/orders/${id}`),
};

export const reservationApi = {
  getAll:       (params)       => api.get('/admin/reservations', { params }),
  updateStatus: (id, status)   => api.put(`/admin/reservations/${id}`, { status }),
};

export const galleryApi = {
  getAll:  (params)   => api.get('/admin/gallery', { params }),
  upload:  (formData) => api.post('/admin/gallery', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:  (id)       => api.delete(`/admin/gallery/${id}`),
};

export const settingsApi = {
  get:    () => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', data),
  uploadImage: (key, file) => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('key', key);
    return api.post('/admin/settings/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const couponApi = {
  getAll:  ()         => api.get('/admin/coupons'),
  create:  (data)     => api.post('/admin/coupons', data),
  update:  (id, data) => api.put(`/admin/coupons/${id}`, data),
  delete:  (id)       => api.delete(`/admin/coupons/${id}`),
};

export const reviewApi = {
  getAll:  (menuItemId) => api.get(`/admin/reviews/${menuItemId}`),
  update:  (id, data)   => api.put(`/admin/reviews/${id}`, data),
  delete:  (id)         => api.delete(`/admin/reviews/${id}`),
};

// ── Super Admin APIs ──────────────────────────────────────────────────────────
export const superAdminApi = {
  login:                (data)                   => superApi.post('/super/login', data),
  getDashboard:         ()                        => superApi.get('/super/dashboard'),
  getRestaurants:       ()                        => superApi.get('/super/restaurants'),
  createRestaurant:     (data)                    => superApi.post('/super/restaurants', data),
  updateRestaurant:     (id, data)                => superApi.put(`/super/restaurants/${id}`, data),
  deleteRestaurant:     (id)                      => superApi.delete(`/super/restaurants/${id}`),
  getRestaurantStats:   (id)                      => superApi.get(`/super/restaurants/${id}/stats`),
  // Password management
  resetSuperAdminPassword: (data)                 => superApi.put('/super/reset-password', data),
  getRestaurantAdmins:  (restaurantId)            => superApi.get(`/super/restaurants/${restaurantId}/admins`),
  resetAdminPassword:   (restaurantId, adminId, data) => superApi.put(`/super/restaurants/${restaurantId}/admins/${adminId}/password`, data),
  // Email change (2-step)
  requestEmailChange:   (data)                    => superApi.post('/super/change-email/request', data),
  getActivityLogs:      (params)                  => superApi.get('/super/activity-logs', { params }),
  confirmEmailChange:   (token)                   => superApi.get(`/super/change-email/confirm?token=${token}`),
};

export const contactApi = {
  getAll:    ()    => api.get('/admin/contact'),
  markRead:  (id)  => api.put(`/admin/contact/${id}/read`),
  markAllRead: ()  => api.put('/admin/contact/read-all'),
  delete:    (id)  => api.delete(`/admin/contact/${id}`),
};

export default api;
